import AddCircleIcon from "@mui/icons-material/AddCircle";
import LoadingButton from "@mui/lab/LoadingButton";
import { Button, FormGroup, List, Modal, Typography } from "@mui/material";
import { Box } from "@mui/system";
import _ from "lodash";
import * as React from "react";
import styled from "styled-components";
import { useNotification } from "../../contexts/NotifcationContext";
import {
  useGetBundleForManifest,
  useRemovePlanningRule,
  useUpdatePlanningRules,
} from "../../contexts/PublishManagerContext";

import {
  PlanningRule,
  PublishManifestDoc,
} from "../../lib/models/PublishManifest";
import { AppRoutes } from "../../lib/types";
import DataTable from "../DataTable";
import Flex from "../Flex";
import Link from "../Link";
import ModalContent from "../ModalContent";
import Section from "../Section";
import PlanningRuleModal from "./PlanningRuleModal";

type Props = {
  className?: string;
  discordGuildID: string;
  manifest: PublishManifestDoc;
  fragOrderID: string;
};

// The move to PlanItems was a failure, so we need this to easily control if we can have multiple planners.
const ENABLE_MULTIPLE_PLANNERS = false;

function PlanningManagementForm({
  className,
  manifest,

  fragOrderID,
}: Props) {
  const [removing, setRemoving] = React.useState<PlanningRule>(null);
  const [ruleReviewOpen, setRuleReviewOpen] = React.useState<string>(null);
  const update = useUpdatePlanningRules();
  const remove = useRemovePlanningRule();
  const [updating, setUpdating] = React.useState(false);
  const [editing, setEditing] = React.useState<PlanningRule>(null);
  const [creating, setCreating] = React.useState<PlanningRule>(null);
  const { success, error } = useNotification();
  const { data: tasking } = useGetBundleForManifest(manifest);

  const availableGroups = ENABLE_MULTIPLE_PLANNERS
    ? tasking?.plannedGroups
    : _.filter(
        tasking?.plannedGroups,
        (g) =>
          !_.find(manifest.planningRules, (rule) =>
            _.includes(rule.plannableGroupNames, g.name)
          )
      );

  if (!manifest) {
    return (
      <Flex wide center>
        <Flex column align>
          <h4>This Frag Order has not been published.</h4>
          <Link to={`${AppRoutes.FragOrderDetail}/${fragOrderID}/publish`}>
            <Button variant="contained">View Publish Options</Button>
          </Link>
        </Flex>
      </Flex>
    );
  }

  const handleRemove = (rule: PlanningRule) => {
    if (window.confirm("Are you sure you want to remove this rule?")) {
      setRemoving(rule);

      remove(manifest, rule).then(() => setRemoving(null));
    }
  };

  const handleEditSubmit = () => {
    setUpdating(true);

    update(manifest, { ...editing })
      .then(() => {
        success("Planning rules updated.");
        setEditing(null);
      })
      .catch((e) => error(e.message))
      .finally(() => setUpdating(false));
  };

  const handleCreateSubmit = () => {
    if (!creating.discordUserID) {
      error("You must select a user to create a planning rule.");
      return;
    }

    setUpdating(true);

    update(manifest, { ...creating })
      .then(() => {
        success("Planning rules updated.");
        setCreating(null);
      })
      .catch((e) => error(e.message))
      .finally(() => setUpdating(false));
  };

  const relevantRule = _.find(
    manifest.planningRules,
    (r) => r.discordUserID === ruleReviewOpen
  );

  return (
    <div className={className}>
      <DataTable
        columns={[
          { value: "discordDisplayText", label: "Username" },
          {
            value: (r: PlanningRule) => (
              <Button
                variant="outlined"
                onClick={() => setRuleReviewOpen(r.discordUserID)}
              >
                {r.plannableGroupNames.length} groups
              </Button>
            ),
            label: "Plannable Groups",
          },
          {
            label: "",
            width: 64,
            value: (r: PlanningRule) => (
              <Button
                variant="outlined"
                onClick={() => setEditing(_.cloneDeep(r))}
              >
                Edit
              </Button>
            ),
          },
          {
            value: (r: PlanningRule) => (
              <LoadingButton
                loading={
                  removing?.discordUserID === r.discordUserID ||
                  removing?.discordRoleID === r.discordRoleID
                }
                onClick={() => handleRemove(r)}
                variant="outlined"
                color="error"
              >
                Remove
              </LoadingButton>
            ),
            label: "",
            width: 64,
          },
        ]}
        rows={manifest.planningRules}
      />
      <FormGroup>
        <Box marginBottom={4}>
          <Box marginTop={2}>
            <Button
              type="button"
              variant="outlined"
              onClick={() => {
                setCreating({
                  discordDisplayText: "",
                  discordUserID: null,
                  plannableGroupNames: [],
                  isRoleRule: false,
                  discordRoleID: null,
                });
              }}
              startIcon={<AddCircleIcon />}
            >
              Add Planning Rule
            </Button>
          </Box>
        </Box>
      </FormGroup>

      <PlanningRuleModal
        open={!!editing}
        onClose={() => setEditing(null)}
        actionType="edit"
        updating={updating}
        onChange={(rule) => setEditing({ ...editing, ...rule })}
        onSubmit={handleEditSubmit}
        rule={editing}
        groups={_.map(availableGroups, "name")}
      />
      <PlanningRuleModal
        open={!!creating}
        onClose={() => setCreating(null)}
        actionType="create"
        updating={updating}
        onChange={(rule) => setCreating({ ...editing, ...rule })}
        onSubmit={handleCreateSubmit}
        rule={creating}
        groups={_.map(availableGroups, "name")}
        guildID={manifest.discordGuildID}
      />
      <Modal open={!!ruleReviewOpen} onClose={() => setRuleReviewOpen(null)}>
        <ModalContent style={{ minWidth: 480 }}>
          <Section title="Planning Rule Details">
            <Typography fontWeight={600}>
              Planner: {relevantRule?.discordDisplayText}
            </Typography>
            <Box mt={2}>
              <Typography>Groups:</Typography>
              <List>
                {_.map(relevantRule?.plannableGroupNames, (g) => (
                  <li key={g}>{g}</li>
                ))}
              </List>
            </Box>
          </Section>
        </ModalContent>
      </Modal>
    </div>
  );
}

export default styled(PlanningManagementForm).attrs({
  className: PlanningManagementForm.name,
})`
  .MuiAutocomplete-root {
    min-width: 360px;
    /* margin-right: 36px; */
  }

  .MuiInputBase-root {
    min-width: 120px;
  }

  .MuiFormControl-root {
    box-sizing: border-box;
  }

  .MuiButton-root {
    text-decordation: none;
  }

  .MuiFormGroup-root .MuiBox-root {
    padding: 0;
  }
`;
